import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import viewImage from "../../assets/images/icons/view.svg";
import editImage from "../../assets/images/icons/edit.svg";
import FilterTrigger from "../../commons/FilterTrigger/FilterTrigger";
import FilterOptionsMenu from "../../commons/FilterOptionsMenu/FilterOptionsMenu";
import DateRangeSelectButton from "../../commons/DateRangeSelectButton/DateRangeSelectButton";
import TablePagination from "../../commons/Pagination/TablePagination";
import CopyableId from "../../commons/CopyableId/CopyableId";
import { Modal, Button } from "react-bootstrap";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";
import { toast } from "react-toastify";

const STATUS_FILTER_OPTIONS = [
  "All",
  "Pending",
  "Processing",
  "Completed",
  "Failed",
];

const STATUS_LABELS = {
  0: "Pending",
  1: "Processing",
  2: "Completed",
  3: "Failed",
};

const STATUS_FILTER_MAP = {
  Pending: 0,
  Processing: 1,
  Completed: 2,
  Failed: 3,
};

const TYPE_FILTER_OPTIONS = ["All", "Restaurant", "Driver", "Admin"];

const TYPE_FILTER_MAP = {
  Restaurant: "restaurant",
  Driver: "driver",
  Admin: "admin",
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const text = String(value);
    return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatUserType = (type) => {
  if (!type) return "N/A";
  return String(type).charAt(0).toUpperCase() + String(type).slice(1);
};

const formatStatus = (status) => {
  if (status === null || status === undefined || status === "") return "N/A";
  if (STATUS_LABELS[status] !== undefined) return STATUS_LABELS[status];
  return String(status);
};

const mapPayoutRow = (item) => ({
  id: item?._id,
  payoutRefId: item?.payout_ref_id || "N/A",
  settlementPeriod:
    item?.start_date && item?.end_date
      ? `${formatDate(item.start_date)} - ${formatDate(item.end_date)}`
      : "N/A",
  userType: formatUserType(item?.type),
  orders: item?.number_of_order ?? "N/A",
  restaurants: item?.total_restaurants ?? "N/A",
  netPayout: formatMoney(item?.total_net_payable),
  status: formatStatus(item?.status),
  statusCode: item?.status,
  created: formatDate(item?.createdAt),
  scheduleDate: formatDate(item?.schedule_date),
  scheduleDateRaw: item?.schedule_date || null,
});

const parsePayoutList = (payload) => {
  const list = payload?.payout;
  return Array.isArray(list) ? list.map(mapPayoutRow) : [];
};

const getStatusBadgeClass = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "processing") return "badge-info";
  if (normalized === "pending") return "badge-warning";
  if (normalized === "completed" || normalized === "success" || normalized === "paid") {
    return "badge-success";
  }
  if (normalized === "failed" || normalized === "rejected") return "badge-danger";
  return "badge-secondary";
};

function ScheduledPayouts() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("All");
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [typeFilter, setTypeFilter] = useState("All");
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [showAppliedRange, setShowAppliedRange] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rescheduleModal, setRescheduleModal] = useState({
    open: false,
    payout: null,
    scheduleDate: "",
  });
  const [rescheduleSubmitting, setRescheduleSubmitting] = useState(false);
  const limit = 10;

  const statusFilterRef = useRef(null);
  const typeFilterRef = useRef(null);

  const fetchPayouts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });

      if (statusFilter !== "All" && STATUS_FILTER_MAP[statusFilter] !== undefined) {
        params.set("status", String(STATUS_FILTER_MAP[statusFilter]));
      }

      if (typeFilter !== "All" && TYPE_FILTER_MAP[typeFilter]) {
        params.set("type", TYPE_FILTER_MAP[typeFilter]);
      }

      if (fromdate && todate) {
        params.set("fromdate", fromdate);
        params.set("todate", todate);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.listOfPayout}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        setBatches(parsePayoutList(payload));
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              payload?.totalPages ||
              payload?.total_pages ||
              1
          ) || 1
        );
      } else {
        setBatches([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching payouts:", error);
      setBatches([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, typeFilter, fromdate, todate]);

  useEffect(() => {
    fetchPayouts();
  }, [fetchPayouts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) {
        setShowStatusFilter(false);
      }
      if (typeFilterRef.current && !typeFilterRef.current.contains(event.target)) {
        setShowTypeFilter(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApplyDateRange = (start, end) => {
    setFromdate(start);
    setTodate(end);
    setShowAppliedRange(true);
    setPage(1);
  };

  const handleResetDateRange = () => {
    setFromdate(null);
    setTodate(null);
    setShowAppliedRange(false);
    setPage(1);
  };

  const handleView = (item) => {
    if (!item?.id) return;
    navigate(`/payout-management/scheduled-payouts/${item.id}`, {
      state: {
        payoutRefId: item.payoutRefId,
        userType: item.userType,
        settlementPeriod: item.settlementPeriod,
      },
    });
  };

  const handleOpenReschedule = (item) => {
    if (!item?.id) return;
    setRescheduleModal({
      open: true,
      payout: item,
      scheduleDate: toDateInputValue(item.scheduleDateRaw),
    });
  };

  const handleCloseReschedule = () => {
    if (rescheduleSubmitting) return;
    setRescheduleModal({ open: false, payout: null, scheduleDate: "" });
  };

  const handleRescheduleSubmit = async (e) => {
    e.preventDefault();
    const payoutId = rescheduleModal.payout?.id;
    const scheduleDate = rescheduleModal.scheduleDate;

    if (!payoutId) {
      toast.error("Payout ID not found");
      return;
    }
    if (!scheduleDate) {
      toast.error("Please select a schedule date");
      return;
    }

    setRescheduleSubmitting(true);
    try {
      const response = await handleApiCall("post", endpoints.payoutReschedule, {
        payout_id: payoutId,
        schedule_date: scheduleDate,
      });

      if (
        response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS ||
        response?.data?.code === 200 ||
        response?.data?.success
      ) {
        toast.success(response?.data?.message || "Payout rescheduled successfully");
        setRescheduleModal({ open: false, payout: null, scheduleDate: "" });
        fetchPayouts();
      }
    } catch (error) {
      console.error("Error rescheduling payout:", error);
    } finally {
      setRescheduleSubmitting(false);
    }
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Scheduled Payouts</h3>
            </div>

            {showAppliedRange && fromdate && todate && (
              <p className="text-muted small mb-3">
                Showing data from {fromdate} to {todate}
              </p>
            )}

            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <div className="d-flex align-items-center col-md-12 px-0 flex-wrap">
                  <div className="d-flex align-items-center ml-auto mt-2 mt-md-0 flex-nowrap">
                    <div
                      ref={typeFilterRef}
                      className="d-flex align-items-center position-relative mr-3"
                    >
                      <FilterTrigger
                        active={typeFilter !== "All"}
                        onClick={() => {
                          setShowTypeFilter((prev) => !prev);
                          setShowStatusFilter(false);
                        }}
                      >
                        {showTypeFilter && (
                          <FilterOptionsMenu
                            options={TYPE_FILTER_OPTIONS}
                            value={typeFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setTypeFilter(option);
                              setShowTypeFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowTypeFilter((prev) => !prev);
                          setShowStatusFilter(false);
                        }}
                      >
                        {typeFilter === "All" ? "All types" : typeFilter}
                      </span>
                    </div>

                    <div
                      ref={statusFilterRef}
                      className="d-flex align-items-center position-relative mr-3"
                    >
                      <FilterTrigger
                        active={statusFilter !== "All"}
                        onClick={() => {
                          setShowStatusFilter((prev) => !prev);
                          setShowTypeFilter(false);
                        }}
                      >
                        {showStatusFilter && (
                          <FilterOptionsMenu
                            options={STATUS_FILTER_OPTIONS}
                            value={statusFilter}
                            style={{ right: 0, top: "calc(100% + 8px)" }}
                            onSelect={(option) => {
                              setStatusFilter(option);
                              setShowStatusFilter(false);
                              setPage(1);
                            }}
                          />
                        )}
                      </FilterTrigger>
                      <span
                        className="ml-2 text-white"
                        role="button"
                        onClick={() => {
                          setShowStatusFilter((prev) => !prev);
                          setShowTypeFilter(false);
                        }}
                      >
                        {statusFilter === "All" ? "All statuses" : statusFilter}
                      </span>
                    </div>

                    <DateRangeSelectButton
                      appliedFrom={fromdate}
                      appliedTo={todate}
                      onApply={handleApplyDateRange}
                      onReset={handleResetDateRange}
                    />
                  </div>
                </div>
              </div>

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim">
                    <thead>
                      <tr>
                        <th>Payout ID</th>
                        <th>Settlement Period</th>
                        <th>User type</th>
                        <th>Orders</th>
                        <th>Restaurants</th>
                        <th>Schedule</th>
                        <th>Net Payout</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">
                                {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : batches.length > 0 ? (
                        batches.map((item) => (
                          <tr key={item.id}>
                            <td className="text-left">
                              <CopyableId value={item.payoutRefId} />
                            </td>
                            <td>{item.settlementPeriod}</td>
                            <td>{item.userType}</td>
                            <td>{item.orders}</td>
                            <td>{item.restaurants}</td>
                            <td>{item.scheduleDate}</td>
                            <td>{item.netPayout}</td>
                            <td>
                              <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                                {item.status}
                              </span>
                            </td>
                            <td>{item.created}</td>
                            <td>
                              <div className="d-flex align-items-center justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mx-2"
                                  title="View"
                                  onClick={() => handleView(item)}
                                >
                                  <img src={viewImage} alt="View" width="25" height="25" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 mx-2"
                                  title="Reschedule"
                                  onClick={() => handleOpenReschedule(item)}
                                >
                                  <img src={editImage} alt="Reschedule" width="25" height="25" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          </div>
        </div>
      </div>

      <Modal show={rescheduleModal.open} onHide={handleCloseReschedule} centered>
        <Modal.Header closeButton className="bg-black">
          <Modal.Title>Reschedule Payout</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-black">
          {rescheduleModal.payout?.payoutRefId ? (
            <p className="text-muted mb-3">
              Payout: {rescheduleModal.payout.payoutRefId}
            </p>
          ) : null}
          <form onSubmit={handleRescheduleSubmit}>
            <div className="form-group mb-3">
              <label>Schedule Date</label>
              <input
                type="date"
                className="form-control"
                value={rescheduleModal.scheduleDate}
                onChange={(e) =>
                  setRescheduleModal((prev) => ({
                    ...prev,
                    scheduleDate: e.target.value,
                  }))
                }
                required
                disabled={rescheduleSubmitting}
                style={{ colorScheme: "dark" }}
              />
            </div>
            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                onClick={handleCloseReschedule}
                className="mr-2"
                disabled={rescheduleSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={rescheduleSubmitting}>
                {rescheduleSubmitting ? "Saving..." : "Reschedule"}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ScheduledPayouts;
